import { traceSmoothStroke, smoothStrokeHitPoints } from './smoothStroke.js'
import { drawingColor } from './drawingColor.js'
import { createDrawingProjection, segmentDistance, strokeTouchesBox } from './freehandGeometry.js'
import { chartTimeToCoordinate } from '../../../lib/chart/chartTimeProjection.js'
// Erevis annotations use the same LWC series-primitive boundary as earnings,
// pivots and QuickMeasure. Dates/prices are authoritative anchors, never pixels.
export class AnnotationPrimitive {
  constructor(timeframe, theme = 'light') {
    this.theme = theme
    this.timeframe = timeframe
    this.items = []
    this.selected = null
    this.strokeCache = new WeakMap()
    this.strokeBasis = null
    this.view = { zOrder: () => 'normal', renderer: () => ({ draw: target => target.useMediaCoordinateSpace(scope => this.draw(scope)) }) }
  }
  attached({ chart, series, requestUpdate }) { Object.assign(this, { chart, series, requestUpdate }) }
  detached() { clearTimeout(this.rasterTimer); this.penBitmap = null; this.strokeCache = new WeakMap(); this.strokeBasis = null; this.chart = this.series = this.requestUpdate = null }
  paneViews() { return [this.view] }
  updateAllViews() {}
  setTheme(theme) { if (theme !== this.theme) { this.theme = theme; this.penBitmap = null; this.requestUpdate?.() } }
  update(items, selected) { this.items = items; this.selected = selected; this.requestUpdate?.() }
  project(point) {
    const scale = this.chart?.timeScale()
    let x = chartTimeToCoordinate(this.chart, this.series, point.time, this.timeframe)
    // A daily anchor may lie inside a weekly bar. Project onto that bar without
    // pinning out-of-history drawings to the first/last visible candle.
    if (x == null && scale) {
      const bars = this.series?.data?.() || []
      if (bars.length && point.time >= bars[0].time && point.time <= bars.at(-1).time) {
        let low = 0, high = bars.length - 1
        while (low < high) {
          const middle = Math.ceil((low + high) / 2)
          if (bars[middle].time <= point.time) low = middle
          else high = middle - 1
        }
        x = scale.timeToCoordinate(bars[low].time)
      }
    }
    const y = this.series?.priceToCoordinate(point.price)
    return x == null || y == null ? null : { x, y }
  }
  geometry(item, projection) {
    const penProjection = item.kind === 'pen' ? projection || createDrawingProjection({ chart: this.chart, series: this.series }, this.timeframe) : null
    return item.points.map(point => {
      const projected = penProjection ? penProjection.project(point) : this.project(point)
      return projected && point.corner ? { ...projected, corner: true } : projected
    })
  }
  selectBox(a, b) {
    const projection = createDrawingProjection({ chart: this.chart, series: this.series }, this.timeframe)
    return this.items.filter(item => {
      const raw = this.geometry(item, projection)
      const points = item.kind === 'pen' ? smoothStrokeHitPoints(raw.filter(Boolean)) : raw, [start,end] = points
      if (!start) return false
      if (item.kind === 'horizontal') return start.y >= Math.min(a.y,b.y) && start.y <= Math.max(a.y,b.y)
      if (item.kind === 'rectangle' && end) return Math.min(start.x,end.x) <= Math.max(a.x,b.x) && Math.max(start.x,end.x) >= Math.min(a.x,b.x)
        && Math.min(start.y,end.y) <= Math.max(a.y,b.y) && Math.max(start.y,end.y) >= Math.min(a.y,b.y)
      return strokeTouchesBox(points, a, b)
    }).map(item => item.id)
  }
  pick(x, y) {
    const projection = createDrawingProjection({ chart: this.chart, series: this.series }, this.timeframe)
    for (const item of [...this.items].reverse()) {
      const raw = this.geometry(item, projection)
      const points = item.kind === 'pen' ? smoothStrokeHitPoints(raw.filter(Boolean)) : raw, [a, b] = points
      if (item.kind === 'pen') {
        if (points.some((p, i) => p && segmentDistance({x,y}, p, points[i+1] || p) <= Math.max(6, (item.width || 2)/2 + 3))) return item.id
        continue
      }
      if (!a) continue
      if (item.kind === 'horizontal' && Math.abs(y - a.y) < 7) return item.id
      if (item.kind === 'text' && x >= a.x - 5 && x <= a.x + Math.max(30, item.text.length * 8) && Math.abs(y - a.y) < 18) return item.id
      if (!b) continue
      if (item.kind === 'rectangle') {
        const left = Math.min(a.x, b.x), right = Math.max(a.x, b.x), top = Math.min(a.y, b.y), bottom = Math.max(a.y, b.y)
        if (x >= left - 7 && x <= right + 7 && y >= top - 7 && y <= bottom + 7) return item.id
      } else if (item.kind === 'circle') {
        const rx = Math.max(3, Math.abs(b.x - a.x) / 2), ry = Math.max(3, Math.abs(b.y - a.y) / 2)
        if (Math.abs(Math.hypot((x - (a.x + b.x) / 2) / rx, (y - (a.y + b.y) / 2) / ry) - 1) < 0.3) return item.id
      } else {
        const dx = b.x - a.x, dy = b.y - a.y
        const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / (dx * dx + dy * dy || 1)))
        if (Math.hypot(x - a.x - t * dx, y - a.y - t * dy) < 8) return item.id
      }
      if (Math.hypot(x - a.x, y - a.y) < 8 || Math.hypot(x - b.x, y - b.y) < 8) return item.id
    }
    return null
  }
  penTransform(projection) {
    if (typeof Path2D === 'undefined' || typeof DOMMatrix === 'undefined') return null
    const first = this.items.find(item => item.kind === 'pen')?.points[0]
    if (!first) return null
    const clock = projection.clockKey(), scale = this.chart.timeScale()
    const price = first.price, span = Math.max(Math.abs(price) * 0.1, 1)
    const basis = { clock, price, span, x0: scale.logicalToCoordinate(0), x1: scale.logicalToCoordinate(1),
      y0: this.series.priceToCoordinate(price), y1: this.series.priceToCoordinate(price + span), y2: this.series.priceToCoordinate(price + span / 2) }
    const old = this.strokeBasis
    let sx = old ? (basis.x1-basis.x0)/(old.x1-old.x0) : 1
    let sy = old ? (basis.y1-basis.y0)/(old.y1-old.y0) : 1
    let dx = old ? basis.x0-sx*old.x0 : 0, dy = old ? basis.y0-sy*old.y0 : 0
    // Rebuild only for a changed clock/price transform, including log toggles.
    // Pan/zoom transforms use native Path2D.addPath so stroke width stays in CSS px.
    if (!old || old.clock!==clock || old.price!==price || !Number.isFinite(sx) || !Number.isFinite(sy)
      || Math.abs(basis.y2-(old.y2*sy+dy))>0.01) {
      this.strokeCache=new WeakMap();this.penBitmap=null;this.strokeBasis=basis;sx=sy=1;dx=dy=0
    }
    return { matrix: new DOMMatrix([sx,0,0,sy,dx,dy]), sx,sy,dx,dy }
  }
  drawPen(ctx, item, projection, transform) {
    let cached=this.strokeCache.get(item)
    // In-progress strokes mutate points; committed strokes are immutable.
    if (item._draft || !cached || cached.count!==item.points.length) {
      const points=this.geometry(item,projection).filter(Boolean)
      if(!points.length)return
      const path=new Path2D()
      const inverse=p=>({...p,x:(p.x-transform.dx)/transform.sx,y:(p.y-transform.dy)/transform.sy})
      traceSmoothStroke(path, points.map(inverse))
      cached={path,count:item.points.length};this.strokeCache.set(item,cached)
    }
    const path=new Path2D();path.addPath(cached.path,transform.matrix);ctx.stroke(path)
  }
  drawPenLayer(ctx, mediaSize, projection, transform) {
    if (!transform || typeof document === 'undefined') return false
    const items=this.items.filter(item=>item.kind==='pen' && !item._draft && !item.text && item.points.length>1)
    if(!items.length){this.penBitmap=null;return false}
    const ratio=Math.min(2,Math.abs(ctx.getTransform?.().a || 1))
    const width=mediaSize.width,height=mediaSize.height
    let cache=this.penBitmap
    let sx=cache?transform.sx/cache.transform.sx:1,sy=cache?transform.sy/cache.transform.sy:1
    let dx=cache?transform.dx-sx*cache.transform.dx:0,dy=cache?transform.dy-sy*cache.transform.dy:0
    const same=cache && cache.width===width && cache.height===height && cache.ratio===ratio && cache.items.length===items.length && cache.items.every((item,i)=>item===items[i])
    const covered=cache && -width/2*sx+dx<=0 && width*1.5*sx+dx>=width && -height/2*sy+dy<=0 && height*1.5*sy+dy>=height
    if(!same || !covered || sx<0.5 || sx>2 || sy<0.5 || sy>2){
      clearTimeout(this.rasterTimer);this.rasterTimer=null
      const canvas=document.createElement('canvas');canvas.width=Math.ceil(width*2*ratio);canvas.height=Math.ceil(height*2*ratio)
      const buffer=canvas.getContext('2d');if(!buffer)return false
      buffer.scale(ratio,ratio);buffer.translate(width/2,height/2)
      for(const item of items){buffer.strokeStyle=drawingColor(item.color,this.theme);buffer.lineWidth=item.width||2;buffer.globalAlpha=(item.opacity??100)/100;buffer.lineCap='round';buffer.lineJoin='round';buffer.setLineDash(item.dashed?[3,5]:[]);this.drawPen(buffer,item,projection,transform)}
      cache={canvas,width,height,ratio,items,transform};this.penBitmap=cache;sx=sy=1;dx=dy=0
    }
    // Reuse a bounded raster while navigating. Re-rasterize after the gesture
    // settles for exact pixel thickness and sharpness at the new scale.
    if(Math.abs(sx-1)>0.001 || Math.abs(sy-1)>0.001){
      clearTimeout(this.rasterTimer)
      this.rasterTimer=setTimeout(()=>{this.rasterTimer=null;this.penBitmap=null;this.requestUpdate?.()},120)
    }
    ctx.save();ctx.translate(dx,dy);ctx.scale(sx,sy);ctx.drawImage(cache.canvas,-width/2,-height/2,width*2,height*2);ctx.restore()
    return true
  }
  draw({ context: ctx, mediaSize }) {
    if (this.selectionBox) { const [a,b]=this.selectionBox;ctx.save();ctx.strokeStyle='#2962ff';ctx.lineWidth=1;ctx.setLineDash([4,3]);ctx.strokeRect(a.x,a.y,b.x-a.x,b.y-a.y);ctx.restore() }
    const projection = this.items.some(item => item.kind === 'pen') ? createDrawingProjection({ chart: this.chart, series: this.series }, this.timeframe) : null
    const transform = projection ? this.penTransform(projection) : null
    const penLayer = projection && this.drawPenLayer(ctx,mediaSize,projection,transform)
    for (const item of this.items) {
      const fastPen = item.kind === 'pen' && !item.text && transform && item.points.length > 1
      const selected = item.id === this.selected || (Array.isArray(this.selected) && this.selected.includes(item.id))
      const points = fastPen ? (selected ? [projection.project(item.points[0]),projection.project(item.points.at(-1))] : []) : this.geometry(item, projection)
      const [a, b] = points
      if (!a && !fastPen) continue
      ctx.save(); ctx.strokeStyle = drawingColor(item.color,this.theme); ctx.fillStyle = drawingColor(item.color,this.theme); ctx.lineWidth = item.width || 2; ctx.globalAlpha = (item.opacity ?? 100) / 100
      ctx.font = `${item.italic ? 'italic ' : ''}${item.bold ? 'bold ' : ''}${item.fontSize || 16}px -apple-system, BlinkMacSystemFont, sans-serif`
      ctx.setLineDash(item.dashed ? [3, 5] : [])
      let textStart = a, textEnd = b
      if (fastPen) {
        if (!penLayer || item._draft) { ctx.lineCap='round';ctx.lineJoin='round';this.drawPen(ctx,item,projection,transform) }
      } else if (item.kind === 'pen') {
        ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath()
        traceSmoothStroke(ctx, points.filter(Boolean))
        if (points.length === 1) { ctx.arc(a.x,a.y,(item.width || 2)/2,0,Math.PI*2); ctx.fill() } else ctx.stroke()
      } else if (item.kind === 'line' && b) {
        const left = a.x <= b.x ? a : b, right = a.x <= b.x ? b : a
        let start = left, end = right
        const slope = (right.y - left.y) / (right.x - left.x || 1)
        if (['left', 'both'].includes(item.extend)) start = { x: 0, y: left.y - left.x * slope }
        if (['right', 'both'].includes(item.extend)) end = { x: mediaSize.width, y: right.y + (mediaSize.width - right.x) * slope }
        ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke()
      } else if (item.kind === 'rectangle' && b) {
        const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y), width = Math.abs(b.x - a.x), height = Math.abs(b.y - a.y)
        if (item.fillVisible !== false) { ctx.fillStyle = drawingColor(item.fillColor || item.color,this.theme); ctx.globalAlpha = (item.fillOpacity ?? 15) / 100; ctx.fillRect(x, y, width, height) }
        ctx.globalAlpha = (item.opacity ?? 100) / 100
        if (item.borderVisible !== false) ctx.strokeRect(x, y, width, height)
        const vertical = item.textVertical || 'middle'
        textStart = { x: x + 6, y: vertical === 'top' ? y + (item.fontSize || 16) + 5 : vertical === 'bottom' ? y + height - (item.fontSize || 16) - 11 : y + height / 2 }
        textEnd = { x: x + width - 6, y: textStart.y }
      } else if (item.kind === 'circle' && b) {
        ctx.beginPath(); ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.max(3, Math.abs(b.x - a.x) / 2), Math.max(3, Math.abs(b.y - a.y) / 2), 0, 0, Math.PI * 2); ctx.stroke()
      } else if (item.kind === 'horizontal') {
        textStart = { x: 8, y: a.y }; textEnd = { x: mediaSize.width - 8, y: a.y }
      }
      if (item.text) {
        if (item.kind === 'rectangle') ctx.globalAlpha = 1
        ctx.fillStyle = drawingColor(item.textColor || item.color,this.theme)
        const left = !textEnd || textStart.x <= textEnd.x ? textStart : textEnd
        const right = !textEnd ? textStart : textStart.x <= textEnd.x ? textEnd : textStart
        const horizontal = item.kind === 'text' ? 'left' : item.textHorizontal || 'center'
        const t = horizontal === 'left' ? 0 : horizontal === 'right' ? 1 : 0.5
        ctx.translate(left.x + t * (right.x - left.x), left.y + t * (right.y - left.y))
        if (item.kind === 'line') ctx.rotate(Math.atan2(right.y - left.y, right.x - left.x))
        ctx.textAlign = horizontal
        const vertical = item.textVertical || 'top', size = item.fontSize || 16
        const lines = item.text.split('\n')
        lines.forEach((line, index) => ctx.fillText(line, 0, (vertical === 'bottom' ? size + 5 : vertical === 'middle' ? size / 3 : -5 - (lines.length - 1) * size) + index * size))
      }
      ctx.restore()
      if (item.id === this.selected || (Array.isArray(this.selected) && this.selected.includes(item.id))) for (const point of (item.kind === 'rectangle' && b ? [a, b, { x: a.x, y: b.y }, { x: b.x, y: a.y }] : [a, b]).filter(Boolean)) {
        ctx.save(); ctx.fillStyle = drawingColor(item.color,this.theme); ctx.beginPath(); ctx.arc(point.x, point.y, 4, 0, Math.PI * 2); ctx.fill(); ctx.restore()
      }
    }
  }
}

export function normalizeAnnotations(value) {
  if (!Array.isArray(value)) return []
  return value.slice(-200).filter(item => item && typeof item.id === 'string'
    && ['pen', 'line', 'horizontal', 'circle', 'text', 'rectangle'].includes(item.kind)
    && /^#[0-9a-f]{6}$/i.test(item.color) && typeof item.text === 'string' && item.text.length <= 160
    && Array.isArray(item.points) && (item.kind === 'pen' ? item.points.length > 0 && item.points.length <= 2048 : item.points.length === (['line', 'circle', 'rectangle'].includes(item.kind) ? 2 : 1))
    && item.points.every(p => Number.isFinite(p?.time) && Math.abs(p.time) < 8640000000000 && Number.isFinite(p?.price)))
    .map(item => ({ id: item.id, kind: item.kind, points: item.points.map(({ time, price, corner }) => ({ time, price, ...(corner === true ? { corner: true } : {}) })), ...drawingStyle(item) }))
}


const STYLE_KEYS = ['borderVisible', 'fillVisible', 'fillColor', 'fillOpacity', 'color', 'opacity', 'width', 'dashed', 'extend', 'text', 'textColor', 'fontSize', 'bold', 'italic', 'textVertical', 'textHorizontal', 'visible', 'timeframes']
export function drawingStyle(item) {
  const style = Object.fromEntries(STYLE_KEYS.filter(key => item[key] !== undefined).map(key => [key, item[key]]))
  for (const [key, min, max] of [['fillOpacity', 0, 100], ['opacity', 0, 100], ['width', 1, 4], ['fontSize', 10, 32]]) {
    if (style[key] !== undefined) style[key] = Number.isFinite(style[key]) ? Math.min(max, Math.max(min, style[key])) : min
  }
  if (style.fillColor && !/^#[0-9a-f]{6}$/i.test(style.fillColor)) delete style.fillColor
  if (style.textColor && !/^#[0-9a-f]{6}$/i.test(style.textColor)) delete style.textColor
  if (style.text !== undefined) style.text = typeof style.text === 'string' ? style.text.slice(0, 160) : ''
  if (style.timeframes !== undefined) style.timeframes = Array.isArray(style.timeframes) ? style.timeframes.filter(value => typeof value === 'string').slice(0, 10) : []
  return style
}
export function normalizeTemplates(value) {
  if (!Array.isArray(value)) return []
  return value.slice(-100).filter(item => typeof item?.id === 'string' && typeof item.name === 'string' && item.name.length <= 80
    && ['pen', 'line', 'horizontal', 'circle', 'text', 'rectangle'].includes(item.kind) && item.style && /^#[0-9a-f]{6}$/i.test(item.style.color))
    .map(item => ({ id: item.id, name: item.name, kind: item.kind, style: drawingStyle(item.style) }))
}
